//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

import { Container } from '@backmarket/design-system'
import isEmpty from 'lodash.isempty'
import { mapGetters } from 'vuex'

import { prepareJsonLdScript } from '@core/helpers'
import {
  SCHEMA_PRODUCT_IMAGE,
  buildProductSchema,
  generateAlternateLink,
  getMetas,
} from '@core/helpers/head'
import {
  fetchLandingBuyingGuides,
  fetchLandingReviewsList,
  fetchLandingReviewsSummary,
  fetchSEOTags,
} from '@http/endpoints'
import { MAX_RATING } from '@reviews/constants'

import Layout, { fetch as fetchLayout } from '../../components/Layout'

import translations from './ProductLanding.translations'
import Guides from './components/Guides'
import ProductTags from './components/ProductTags'
import Reviews from './components/Reviews'
import SEOText from './components/SEOText'
import TopLanding, { fetch as fetchTopLanding } from './components/TopLanding'

export default {
  components: {
    ProductTags,
    SEOText,
    Reviews,
    Guides,
    Container,
    Layout,
    TopLanding,
  },

  async asyncData(args) {
    const { store, route } = args
    const { getters, dispatch } = store

    // The landing ID is necessary in the next calls. So we need to do that first.
    // Once the URL migration over, we will be able to // everything by reading the URL
    try {
      await Promise.all([fetchLayout(args), fetchTopLanding(args)])
    } catch (error) {
      if (error.status_code === 429) {
        // Make sure to let 429 errors through, in order to improve the caching
        // strategy on Cloudflare side.
        return args.error({ statusCode: 429, message: 'Too Many Requests' })
      }

      return args.error({ statusCode: 404, message: 'Page Not Found' })
    }

    const baseUrl = getters['config/baseURL']
    const buildAlternateLinks = Object.entries(getters['landing/links']).map(
      ([country, link]) => {
        return generateAlternateLink({ link, country, baseUrl })
      },
    )

    const landingId = route.params.uuid || store.getters['landing/landingId']

    const fetchTags = dispatch('http/request', {
      request: fetchSEOTags,
      queryParams: {
        model: 'landing',
        pk: landingId,
      },
    })

    const fetchGuides = dispatch('http/request', {
      request: fetchLandingBuyingGuides,
      pathParams: { landingId },
    })

    const limit = MAX_RATING
    const fetchReviews = dispatch('http/request', {
      request: fetchLandingReviewsList,
      queryParams: { pk: landingId, limit },
    })

    const fetchSummary = dispatch('http/request', {
      request: fetchLandingReviewsSummary,
      queryParams: { pk: landingId },
    })

    try {
      const [
        alternateLinks,
        { payload: tags },
        { payload: guides },
        {
          payload: { results: reviews },
        },
        { payload: summary },
      ] = await Promise.all([
        Promise.all(buildAlternateLinks),
        fetchTags,
        fetchGuides,
        fetchReviews,
        fetchSummary,
      ])

      return {
        alternateLinks,
        tags,
        guides,
        reviews,
        summary,
      }
    } catch {
      // Fails silently
    }

    return {}
  },

  data() {
    return {
      alternateLinks: [],
      guides: [],
      tags: [],
      reviews: [],
      summary: {},
    }
  },

  head() {
    const title = this.metaTitle

    const description = this.$t(translations.metaDescription, {
      productType: title,
      warrantyDuration: this.warrantyDuration,
      coolingOffDays: this.coolingOffDays,
    })

    const image = this.$static(SCHEMA_PRODUCT_IMAGE)

    const path = decodeURIComponent(this.$route.path)

    const productSchema = buildProductSchema({
      title,
      description,
      rate: this.isRatingsAvailable ? this.ratings : {},
      image,
    })

    return {
      title: this.seoTitle,
      meta: [
        ...getMetas({
          title,
          description,
          image,
          'og:type': 'product',
        }),
      ],
      link: [
        ...this.alternateLinks,
        {
          href: `${this.baseURL}${path}`,
          rel: 'canonical',
        },
      ],
      script: prepareJsonLdScript({ productSchema }),
      __dangerouslyDisableSanitizers: ['script'],
    }
  },

  computed: {
    ...mapGetters({
      landingTitle: 'landing/title',
      seoTitle: 'landing/seoTitle',
      metaTitle: 'landing/subtitle',
      ratings: 'rates/landing/get',
      baseURL: 'config/baseURL',
      warrantyDuration: 'config/warrantyDuration',
      coolingOffDays: 'config/coolingOffDays',
    }),

    isRatingsAvailable() {
      return (
        !isEmpty(this.ratings) &&
        this.ratings.averageRate > 0 &&
        this.ratings.reviewsCount > 0
      )
    },
  },
}
